import { ReactNode, useEffect, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useAddresses } from '../../hooks/useAddresses';
import { Address } from '../../interfaces/Address/Address';
import { NewAddressCard } from '../../components/Address/NewAddressCard';
import { AddressCardPlaceholder } from '../../components/Address/AddressCardPlaceholder';
import { AddressCard } from '../../components/Address/AddressCard';

type AddressListProps = {
  title: string;
  topRightSection?: ReactNode;
  addresses: Address[];
  loading: boolean;
  selectedAddressID?: number;
  onChange: (addressID: number) => void;
};

const AddressList = ({
  title,
  topRightSection,
  addresses,
  loading,
  selectedAddressID,
  onChange,
}: AddressListProps) => {
  const itemSize = {
    xs: 12,
    lg: 6,
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1.5,
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: '40px',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
          {title}
        </Typography>

        {topRightSection}
      </Box>

      <Grid container spacing={2} width='100%'>
        <Grid size={itemSize}>
          <NewAddressCard />
        </Grid>

        {loading && !addresses.length
          ? Array(3)
              .fill(null)
              .map((_, i) => (
                <Grid key={i} size={itemSize}>
                  <AddressCardPlaceholder />
                </Grid>
              ))
          : addresses.map((address, i) => (
              <Grid key={i} size={itemSize}>
                <AddressCard
                  address={address}
                  isSelected={selectedAddressID === address.id}
                  onSelect={() => onChange(address.id)}
                />
              </Grid>
            ))}
      </Grid>
    </Box>
  );
};

type CheckoutAddressSelectProps = {
  shippingAddressID?: number;
  billingAddressID?: number;
  onChange: (
    newShippingAddressID?: number,
    newBillingAddressID?: number,
  ) => void;
};

export const CheckoutAddressSelect = ({
  shippingAddressID,
  billingAddressID,
  onChange,
}: CheckoutAddressSelectProps) => {
  const { addresses, loading } = useAddresses();

  const [combined, setCombined] = useState(true);

  useEffect(() => {
    if (addresses.length) {
      onChange(addresses[0].id, addresses[0].id);
    }
  }, [addresses]);

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 4.5,
        width: '100%',
      }}
    >
      <AddressList
        title='Teslimat Adresi'
        topRightSection={
          <FormControlLabel
            control={
              <Checkbox
                size='small'
                checked={combined}
                onChange={() => {
                  setCombined((x) => !x);
                  onChange(
                    shippingAddressID,
                    combined ? billingAddressID : shippingAddressID,
                  );
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                Fatura adresi olarak kullan
              </Typography>
            }
          />
        }
        addresses={addresses}
        loading={loading}
        selectedAddressID={shippingAddressID}
        onChange={(addressID) =>
          onChange(addressID, combined ? addressID : billingAddressID)
        }
      />

      {!combined && (
        <AddressList
          title='Fatura Adresi'
          addresses={addresses}
          loading={loading}
          selectedAddressID={billingAddressID}
          onChange={(addressID) => onChange(shippingAddressID, addressID)}
        />
      )}
    </Box>
  );
};
